import Title_ru_RU from '@/core/misc/decorators/Title_ru_RU';
import serializeSimplified from '@/core/misc/decorators/SerializeSimplified';
import TYPES from '@/core/services/Changelogs/types';

import Core from '@/core/Core';
import BaseEntity from '@/core/misc/BaseEntity';
import DateSimple from '@/core/types/DateSimple';
import SupplyOffer from '@/domains/SupplyOffers/SupplyOffer';
import ProductCharacteristic from '@/domains/Products/ProductCharacteristic';
import Characteristic from '@/domains/Characteristics/Characteristic';
import ProductsService from '@/domains/Products/ProductsService';
import ProductCategory from '@/domains/ProductCategories/ProductCategory';
import ProductCategoriesService from '@/domains/ProductCategories/ProductCategoriesService';
import Unit from '@/domains/Units/Unit';
import Person from '@/domains/Persons/Person';
import Manufacturer from '@/domains/Manufacturers/Manufacturer';
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";
import ProductActualStatus from "@/domains/Products/ProductActualStatus";

import type Partner from '@/domains/Partners/Partner';
import type Company from '@/domains/Companies/Company';

@Title_ru_RU_Class('Товар')
class Product extends BaseEntity {

  static Events = {
    Created: 'product:created',
    Updated: 'product:updated',
    Deleted: 'product:deleted',
  } as const;

  // ---- Основные поля ----

  /** Наименование номенклатуры */
  @Title_ru_RU('Наименование', TYPES.STRING)
  public name: string;

  /** Артикул */
  @Title_ru_RU('Артикул', TYPES.STRING)
  public code: string | null;

  @Title_ru_RU('Описание', TYPES.STRING)
  public description: string | null;

  /** Описание товара для поставщика в актуальном спросе */
  @Title_ru_RU('Описание для поставщика', TYPES.STRING)
  public descriptionForSeller: string | null;

  /** Ответственный */
  @serializeSimplified
  @Title_ru_RU('Ответственный', TYPES.OBJECT)
  public responsible: Person | null;

  @serializeSimplified
  public unit: Unit | null;

  /** Признак: товар или услуга */
  public isService: boolean = false;

  /** Признак: создан ли товар явно (создать товар и т.д.) или нет (см. demandV2, service у shipment) */
  public isCreatedImplicitly: boolean = false;

  /** Осталось дней до истечения срока годности */
  @Title_ru_RU('Срок годности', TYPES.OBJECT, formatExpirationDiff)
  private _expirationDays: number | null = null;

  @Title_ru_RU('Статус актуальности товара', TYPES.STRING)
  public actualStatus: any | undefined;

  // ---- Связанные сущности ----

  /** Производители */
  @serializeSimplified
  @Title_ru_RU('Производитель', TYPES.MANUFACTURERS)
  public manufacturers: Manufacturer[] = [];

  /** Подтверждённые поставщики */

  /** @serializeSimplified не используем, у SupplyOffer свой toJSON,
   * также бек ожидает SupplyOffer целиком */

  @Title_ru_RU('Разрешение на поставку', TYPES.STRING)
  public supplyOffers: SupplyOffer[] = [];

  /** Характеристики */
  @serializeSimplified
  public characteristics: ProductCharacteristic[] = [];

  // Кол-во всех supplyOffer
  // нужен в тендере, в интерфейсе поставщика
  public supplyOffersCount: number;
  // ---- Категория (с каскадным сеттером) ----

  @serializeSimplified
  private _category: ProductCategory | null;

  get category(): ProductCategory | null {
    return this._category
  }

  set category(value: ProductCategory | null) {
    this._category = value;
  }

  toString(): string {
    return this.name;
  }

  /**
   * Устанавливает категорию с загрузкой полного объекта и каскадным обновлением
   * характеристик, признака услуги и единицы измерения.
   * Использовать в диалогах вместо прямого присваивания category.
   */
  async setCategory(value: ProductCategory | null): Promise<void> {
    if (!value) {
      this._category = null;
      return;
    }

    const fullCategory = await ProductCategoriesService.get(value.id, {
      requireAllowedUnits: true,
      requireCharacteristics: true,
    });
    if (!fullCategory) {
      return;
    }

    this._category = fullCategory;
    this.isService = fullCategory.isService;


    this.characteristics = fullCategory.characteristics.map(
      _characteristic => ProductCharacteristic.fromCharacteristic(_characteristic)
    );


    if (Core.utils.isNotEmptyArray(fullCategory.allowedUnits) && fullCategory.allowedUnits.length === 1) {
      this.unit = fullCategory.allowedUnits[0];
    }
  }

  // ---- Вычисляемые геттеры ----

  /**
   * Map характеристик по ID для быстрого доступа.
   * Строится лениво при первом обращении.
   */
  get characteristicsMap(): Map<number, ProductCharacteristic> {
    return new Map(this.characteristics.map(item => [item.characteristic.id, item]));
  }

  getProductCharacteristicById(id: number): ProductCharacteristic | null {
    return this.characteristicsMap.get(id) ?? null;
  }

  // ---- Требования товара к предложению ----

  /** Есть ли требование к остаточному сроку годности. */
  get hasExpirationRequirement(): boolean {
    return Core.utils.number.isNumber(this._expirationDays);
  }

  /**
   * Минимальная дата годности, которая устраивает покупателя.
   * Требование хранится днями остаточного срока — переводим в дату здесь,
   * чтобы арифметика не расползлась по сущностям предложений.
   */
  get requiredExpirationDate(): DateSimple {
    return DateSimple.today().addDays(this._expirationDays!);
  }


  /** Записи, которые действительно являются разрешениями на поставку.
   *  Без статуса разрешения не существует: такие записи заводятся по факту
   *  закупки или приехали из прайса при слиянии, и решать по ним ничего нельзя. */
  get actualSupplyOffers(): SupplyOffer[] {
    return this.supplyOffers.filter(
      _supplyOffer => Core.utils.isNullOrUndefined(_supplyOffer.status) === false
    );
  }

  /**
   * Нужно ли поставщику разрешение на поставку этого товара. Нужно, если на
   * товар заведено хоть одно разрешение, у кого угодно. Нет ни одного —
   * участвовать может любой поставщик с любой связкой.
   *
   * Смотрим на счётчик с бэка, а не на supplyOffers: в интерфейсе поставщика
   * список приходит только по нему самому, и пустой список там не значит,
   * что разрешений на товар нет.
   */
  get isSupplyOfferRequired(): boolean {
    return this.supplyOffersCount > 0;
  }

  /** Все разрешения на поставку этого товара конкретным поставщиком —
   *  одобренные, отклонённые и ещё не рассмотренные. */
  getSupplyOffersByPartner(partner: Partner): SupplyOffer[] {
    return this.actualSupplyOffers.filter(
      _supplyOffer => _supplyOffer.seller?.id === partner.id
    );
  }

  /** Одобренные разрешения — те, по которым поставщик вправе играть,
   *  если его не исключили из торгов отдельно. */
  getApprovedSupplyOffersByPartner(partner: Partner): SupplyOffer[] {
    return this.getSupplyOffersByPartner(partner).filter(
      _supplyOffer => _supplyOffer.isApproved === true
    );
  }

  /** Действующие разрешения на поставку этого товара конкретным поставщиком. */
  getApprovedAndNotExcludedSupplyOffersByPartner(partner: Partner): SupplyOffer[] {
    return this.getApprovedSupplyOffersByPartner(partner).filter(
      _supplyOffer => _supplyOffer.isExcludedFromTenders === false
    );
  }

  getDesiredManufacturersByPartner(partner: Partner): Manufacturer[] {
    const result = new Map<number, Manufacturer>();

    for (const _supplyOffer of this.getApprovedAndNotExcludedSupplyOffersByPartner(partner)) {
      result.set(_supplyOffer.manufacturer.id, _supplyOffer.manufacturer);
    }

    if (result.size > 0) {
      return Array.from(result.values());
    }
    else {
      return this.manufacturers;
    }
  }

  getDesiredSellerCompaniesByPartner(partner: Partner): Company[] {
    const result = new Map<number, Company>();

    for (const _supplyOffer of this.getApprovedAndNotExcludedSupplyOffersByPartner(partner)) {
      result.set(_supplyOffer.sellerCompany.id, _supplyOffer.sellerCompany);
    }

    return Array.from(result.values());
  }

  validateSupplyOffer(
    /** offer.seller / bid.seller — партнёр-поставщик */
    seller: Partner,
    /** offer.sellerCompany / bid.sellerCompany — юр. лицо из формы */
    sellerCompany: Company,
    /** offer.manufacturer / bid.manufacturer — производитель из формы */
    manufacturer: Manufacturer,
  ): string | null {
    if (this.isSupplyOfferRequired === false) {
      /** Разрешение на этот товар не обязательно — валидировать нечего. */
      return null;
    }
    else if (Core.utils.isEmptyArray(this.actualSupplyOffers) === true) {
      /** Разрешений на товар нет вовсе — поставлять может кто угодно и чем угодно. */
      return null;
    }
    else {
      const bundleKey = SupplyOffer.getBundleKey(seller, sellerCompany, manufacturer);

      const isPermitted = this.getApprovedAndNotExcludedSupplyOffersByPartner(seller).some(
        _supplyOffer => _supplyOffer.bundleKey === bundleKey
      );

      if (isPermitted === true) {
        return null;
      }
      else {
        return `Нет разрешения на поставку этого товара по связке `
          + `"${seller.name} — ${sellerCompany.name} — ${manufacturer.name}"`;
      }
    }
  }

  // ---- Статические фабрики ----

  static isProduct(value: unknown): value is Product {
    return value instanceof Product;
  }

  /**
   * Создаёт пустой продукт с текущим пользователем в качестве ответственного
   */
  static getEmpty(): Product {
    const product = new this();
    product.responsible = Core.currentUser;
    return product;
  }

  static fromServer(data: any, product = new Product()): Product {
    super.fromServer(data, product);

    product.responsible = Core.utils.hydrate(data.responsible, Person.fromServer);
    product.unit = Core.utils.hydrate(data.unit, Unit.fromServer);
    product.manufacturers = Core.utils.hydrateArray(data.manufacturers, Manufacturer.fromServer);
    product.supplyOffers = Core.utils.hydrateArray(data.supplyOffers, SupplyOffer.fromServer);
    product.characteristics = Core.utils.hydrateArray(data.characteristics, ProductCharacteristic.fromServer);
    product._category = Core.utils.hydrate(data.category, ProductCategory.fromServer);
    product.actualStatus = Core.utils.hydrate(data.actualStatus, ProductActualStatus.fromServer);

    return product;
  }

  // fixme при написании product.expirationDays.name возникают вопросы. Какое name может быть у дней?
  get expirationDays(): { id: number; name: string } | null {
    return ProductsService.expirationDaysRemainingList.find(
      item => item.id === this._expirationDays
    ) ?? null;
  }

  set expirationDays(value: { id: number } | number | null) {
    this._expirationDays = typeof value === 'object' ? value?.id ?? null : value;
  }

  // ---- Бизнес-логика ----

  /**
   * Формирует HTML-представление характеристик продукта для копирования в буфер.
   */
  copyProductCharacteristics(): string {
    let html = '';

    html += `<p style="font-size: 16px; font-family: DejaVu Sans, sans-serif; line-height: 1;"><strong>${this._category?.name}</strong></p><br>`;
    html += '<ul>';

    html += this.characteristics
      // Изображения в буфер не копируем: встроить картинку в HTML для Word/почты
      // без загрузки файла нельзя,
      .filter(_characteristic => _characteristic.valueType !== Characteristic.VALUE_TYPE_IMAGE)
      .map(_characteristic => {
        const displayValue = _characteristic.valueStringRepresentation ?? '--';

        return `<li>`
          + `<p style="font-family: DejaVu Sans, sans-serif; font-size: 14px; line-height: 1;"><strong>${_characteristic.characteristic.name}:</strong></p>`
          + `<p style="font-family: DejaVu Sans, sans-serif; font-size: 14px; line-height: 1;">&nbsp;&nbsp;&nbsp;&nbsp;${displayValue}</p>`
          + `</li>`;
      })
      .join('\n');

    const expirationName = this.expirationDays?.name ?? null;

    html += `<li>`
      + `<p style="font-size: 14px; font-family: DejaVu Sans, sans-serif; line-height: 1;"><strong>Остаточный срок годности:</strong></p>`
      + `<p style="font-size: 14px; font-family: DejaVu Sans, sans-serif; line-height: 1;">&nbsp;&nbsp;&nbsp;&nbsp;${expirationName ?? 'Срок годности истек либо не установлен'}</p>`
      + `</li>`;

    html += `<li>`
      + `<p style="font-size: 14px; font-family: DejaVu Sans, sans-serif; line-height: 1;"><strong>Описание для поставщика:</strong></p>`
      + `<p style="font-size: 14px; font-family: DejaVu Sans, sans-serif; line-height: 1;">&nbsp;&nbsp;&nbsp;&nbsp;${this.descriptionForSeller ?? '--'}</p>`
      + `</li>`;

    if (Core.utils.isNotEmptyArray(this.manufacturers)) {
      html += `<li>`
        + `<p style="font-size: 14px; font-family: DejaVu Sans, sans-serif; line-height: 1;"><strong>Производители:</strong></p>`;
      html += this.manufacturers
        .map(manufacturer => `<p style="font-size: 14px; font-family: DejaVu Sans, sans-serif; line-height: 1;">&nbsp;&nbsp;&nbsp;&nbsp;${manufacturer.name}</p>`)
        .join('\n');
      html += `</li>`;
    }

    html += `</ul>`;

    return html;
  }

  /**
   * Проверяет, есть ли у продукта заполненные характеристики
   */
  hasCharacteristics(): boolean {
    return Core.utils.isNotEmptyArray(this.characteristics);
  }

  /**
   * Строковое представление производителей через запятую.
   * Возвращает null если производителей нет.
   */
  get manufacturersStringRepresentation(): string | null {
    if (!Array.isArray(this.manufacturers) || this.manufacturers.length === 0) {
      return null;
    }

    return this.manufacturers.map(item => item.name).join(', ');
  }
}

// Вынесено за пределы класса: декораторы полей выполняются в момент определения класса,
// когда Product ещё не существует — ссылка на статический метод класса внутри декоратора невозможна.
// Импортировать ProductsService напрямую нельзя — циклическая зависимость Product → ProductsService → Product.
function formatExpirationDiff(oldValue: any, newValue: any): string {
  if (!oldValue && newValue) {
    return `: ${ProductsService.getExpirationDateDaysRemainingByDays(newValue)?.name}`;
  }
  if (!newValue) {
    return ' был удалён';
  }
  return ` с ${ProductsService.getExpirationDateDaysRemainingByDays(oldValue)?.name}`
    + ` на ${ProductsService.getExpirationDateDaysRemainingByDays(newValue)?.name}`;
}

export default Product;